import { supabase } from '../lib/supabase';

/**
 * Participant Group Service
 * Chair/organizer-managed audience groups for a conference. A group's
 * effective members = (live holders of its role_sources) ∪ (explicit members).
 * Used to target in-app announcements.
 */

export const ROLE_SOURCE_OPTIONS = [
  { value: 'reviewer', label: 'All reviewers' },
  { value: 'author', label: 'All authors' },
  { value: 'chair', label: 'All chairs' },
  { value: 'organizer', label: 'All organizers' }
];

const mapGroup = (g) => ({
  id: g?.id,
  conferenceId: g?.conference_id,
  name: g?.name,
  description: g?.description,
  roleSources: g?.role_sources || [],
  createdAt: g?.created_at,
  memberCount: g?.conference_group_members?.[0]?.count ?? undefined
});

export const groupService = {
  async getGroups(conferenceId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_participant_groups')
        ?.select('*, conference_group_members(count)')
        ?.eq('conference_id', conferenceId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;
      return data?.map(mapGroup) || [];
    } catch (error) {
      console.error('Get groups error:', error);
      throw error;
    }
  },

  async createGroup(conferenceId, { name, description, roleSources = [] }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data, error } = await supabase
        ?.from('conference_participant_groups')
        ?.insert({
          conference_id: conferenceId,
          name,
          description: description || null,
          role_sources: roleSources,
          created_by: user?.id
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return mapGroup(data);
    } catch (error) {
      console.error('Create group error:', error);
      throw error;
    }
  },

  async updateGroup(groupId, { name, description, roleSources }) {
    try {
      const { data, error } = await supabase
        ?.from('conference_participant_groups')
        ?.update({
          name,
          description: description || null,
          role_sources: roleSources || [],
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', groupId)
        ?.select()
        ?.single();

      if (error) throw error;
      return mapGroup(data);
    } catch (error) {
      console.error('Update group error:', error);
      throw error;
    }
  },

  async deleteGroup(groupId) {
    try {
      const { error } = await supabase
        ?.from('conference_participant_groups')
        ?.delete()
        ?.eq('id', groupId);
      if (error) throw error;
    } catch (error) {
      console.error('Delete group error:', error);
      throw error;
    }
  },

  /** Explicit (individually-added) members of a group, with profiles. */
  async getGroupMembers(groupId) {
    try {
      const { data, error } = await supabase
        ?.from('conference_group_members')
        ?.select('user_id, user_profiles (id, first_name, last_name, email, institution)')
        ?.eq('group_id', groupId);

      if (error) throw error;
      return data?.map(m => ({
        id: m?.user_profiles?.id || m?.user_id,
        firstName: m?.user_profiles?.first_name,
        lastName: m?.user_profiles?.last_name,
        email: m?.user_profiles?.email,
        institution: m?.user_profiles?.institution
      })) || [];
    } catch (error) {
      console.error('Get group members error:', error);
      throw error;
    }
  },

  async addGroupMember(groupId, userId) {
    try {
      const { error } = await supabase
        ?.from('conference_group_members')
        ?.upsert({ group_id: groupId, user_id: userId }, { onConflict: 'group_id,user_id' });
      if (error) throw error;
    } catch (error) {
      console.error('Add group member error:', error);
      throw error;
    }
  },

  async removeGroupMember(groupId, userId) {
    try {
      const { error } = await supabase
        ?.from('conference_group_members')
        ?.delete()
        ?.eq('group_id', groupId)
        ?.eq('user_id', userId);
      if (error) throw error;
    } catch (error) {
      console.error('Remove group member error:', error);
      throw error;
    }
  },

  /**
   * Everyone associated with the conference (role holders + submission authors),
   * deduped — used to pick individuals to add to a group.
   */
  async getConferenceParticipants(conferenceId) {
    try {
      const [rolesRes, authorsRes] = await Promise.all([
        supabase
          ?.from('conference_roles')
          ?.select('user_id, role, user_profiles (id, first_name, last_name, email, institution)')
          ?.eq('conference_id', conferenceId),
        supabase
          ?.from('conference_submissions')
          ?.select('user_id, user_profiles (id, first_name, last_name, email, institution)')
          ?.eq('conference_id', conferenceId)
      ]);

      if (rolesRes?.error) throw rolesRes?.error;
      if (authorsRes?.error) throw authorsRes?.error;

      const byId = new Map();
      const add = (profile, role) => {
        if (!profile?.id) return;
        const existing = byId?.get(profile?.id);
        const roles = new Set(existing?.roles || []);
        if (role) roles?.add(role);
        byId?.set(profile?.id, {
          id: profile?.id,
          firstName: profile?.first_name,
          lastName: profile?.last_name,
          email: profile?.email,
          institution: profile?.institution,
          roles: Array.from(roles)
        });
      };

      rolesRes?.data?.forEach(r => add(r?.user_profiles, r?.role));
      authorsRes?.data?.forEach(a => add(a?.user_profiles, 'author'));

      return Array.from(byId?.values())?.sort((a, b) =>
        `${a?.firstName} ${a?.lastName}`.localeCompare(`${b?.firstName} ${b?.lastName}`));
    } catch (error) {
      console.error('Get conference participants error:', error);
      throw error;
    }
  },

  /** Resolve the user_ids for a role source within a conference. */
  async resolveRoleSource(conferenceId, source) {
    if (source === 'author') {
      const { data, error } = await supabase
        ?.from('conference_submissions')
        ?.select('user_id')
        ?.eq('conference_id', conferenceId);
      if (error) throw error;
      return data?.map(r => r?.user_id)?.filter(Boolean) || [];
    }
    const { data, error } = await supabase
      ?.from('conference_roles')
      ?.select('user_id')
      ?.eq('conference_id', conferenceId)
      ?.eq('role', source);
    if (error) throw error;
    return data?.map(r => r?.user_id)?.filter(Boolean) || [];
  },

  /**
   * Effective member user_ids for a group:
   * (union of all role_sources holders) ∪ (explicit members).
   */
  async resolveGroupUserIds(conferenceId, group) {
    const ids = new Set();

    for (const source of group?.roleSources || []) {
      const userIds = await this.resolveRoleSource(conferenceId, source);
      userIds?.forEach(id => ids?.add(id));
    }

    const { data, error } = await supabase
      ?.from('conference_group_members')
      ?.select('user_id')
      ?.eq('group_id', group?.id);
    if (error) throw error;
    data?.forEach(m => { if (m?.user_id) ids?.add(m?.user_id); });

    return ids;
  }
};

export default groupService;
